/**
 * Add Elaboration Modal
 *
 * Always shows two explicit pickers (parent + child) regardless of whether
 * opened from an item detail page or from the Elaborations relationship page.
 *
 * When sourceItemId is provided (item-detail context) the parent picker is
 * pre-populated with the current item so the user can see it, confirm the
 * role, or swap it to the child position via the swap button.
 */

import { useState, useEffect, useMemo } from 'react';
import { X, AlertCircle, ArrowUpDown, GitFork } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';
import type { ElaborationSubtype } from '../../../types/prioritization/itemRelationships';
import { getRelationshipGuideEntry } from '../../../lib/relationshipGuide';

interface AddElaborationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (edgeId: string) => void;
  /** Pre-populate the parent picker when opened from an item detail page. */
  sourceItemId?: string;
}

const ELABORATION_SUBTYPES: { value: ElaborationSubtype; label: string; description: string }[] = [
  { value: 'solution_spectrum', label: 'Solution Spectrum', description: 'Sufficiency gradient: minimally sufficient → sufficient → ideal' },
  { value: 'solution_variant', label: 'Solution Variant', description: 'Different approaches at a similar sufficiency level' },
];

const ELABORATION_GUIDE = getRelationshipGuideEntry('elaboration');
const ElaborationIcon = ELABORATION_GUIDE?.Icon ?? GitFork;

export function AddElaborationModal({
  isOpen,
  onClose,
  onCreated,
  sourceItemId,
}: AddElaborationModalProps) {
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);

  // parentItemId = the problem/opportunity being elaborated on (source)
  // childItemId  = one possibility on the elaboration continuum (target)
  const [parentItemId, setParentItemId] = useState<string>(sourceItemId ?? '');
  const [childItemId, setChildItemId] = useState<string>('');
  const [subtype, setSubtype] = useState<ElaborationSubtype>('solution_spectrum');
  const [rationale, setRationale] = useState('');
  const [frameScope, setFrameScope] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // The parent's distinct frame types (its own category lead + alternate frames).
  // When the parent carries 2+ framings, the elaboration can be scoped to the
  // framings under which this child is a valid response (canon 07 Rule 24).
  const parentFrameTypes = useMemo(() => {
    const parent = items.find((i) => i.id === parentItemId);
    if (!parent) return [];
    const types: string[] = [];
    if (parent.itemCategory) types.push(parent.itemCategory);
    for (const frame of parent.frames ?? []) {
      const ft = frame.frameType ?? (frame as { frame_type?: string }).frame_type;
      if (ft && !types.includes(ft)) types.push(ft);
    }
    return types;
  }, [items, parentItemId]);

  const showFrameScope = parentFrameTypes.length >= 2;

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setParentItemId(sourceItemId ?? '');
    setChildItemId('');
    setSubtype('solution_spectrum');
    setRationale('');
    setFrameScope([]);
    setError(null);
  }, [isOpen, sourceItemId]);

  // Reset frame scope whenever the parent changes — scope is parent-specific.
  useEffect(() => {
    setFrameScope([]);
  }, [parentItemId]);

  const handleSwap = () => {
    setParentItemId(childItemId);
    setChildItemId(parentItemId);
  };

  const handleCreate = async () => {
    setError(null);
    if (!parentItemId) {
      setError('Select the parent (problem/opportunity) item');
      return;
    }
    if (!childItemId) {
      setError('Select the child possibility');
      return;
    }
    if (parentItemId === childItemId) {
      setError('An item cannot elaborate on itself');
      return;
    }

    setIsCreating(true);
    try {
      // Only persist a frame scope when it is a genuine subset of the parent's
      // framings. Empty or "all framings" means universal (the default).
      const isUniversal =
        frameScope.length === 0 || frameScope.length === parentFrameTypes.length;
      const metadata = !isUniversal ? { frameScope } : undefined;
      const edge = await createEdge({
        type: 'elaboration',
        subtype,
        directionality: 'directed',
        status: 'active',
        declaredMode: 'declared',
        sourceItemId: parentItemId,
        targetItemId: childItemId,
        rationale: rationale.trim() || undefined,
        ...(metadata ? { metadata } : {}),
      });
      if (edge) {
        onCreated?.(edge.id);
        onClose();
      } else {
        setError('Failed to create elaboration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create elaboration');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const canSubmit = !!parentItemId && !!childItemId && parentItemId !== childItemId && !isCreating;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <ElaborationIcon className="h-6 w-6 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add Elaboration</h2>
              <p className="text-sm text-[var(--text-ter)]">
                {ELABORATION_GUIDE?.tagline ?? 'Possibility spectrum (vertical)'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Parent + child pickers with swap */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                Parent item <span className="text-[var(--text-ter)] font-normal">(the desired outcome / need)</span>
              </label>
              <SearchableItemPicker
                value={parentItemId || undefined}
                onChange={(id) => setParentItemId(id ?? '')}
                excludeId={childItemId || undefined}
                label=""
                placeholder="Search for the parent item..."
                lifecycleStates={[]}
                allowEmpty={false}
                allowCreate
              />
            </div>

            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleSwap}
                title="Swap parent and child roles"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] rounded-full transition-colors"
              >
                <ArrowUpDown className="h-3.5 w-3.5" />
                Swap roles
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                Child item <span className="text-[var(--text-ter)] font-normal">(one possibility on the continuum)</span>
              </label>
              <SearchableItemPicker
                value={childItemId || undefined}
                onChange={(id) => setChildItemId(id ?? '')}
                excludeId={parentItemId || undefined}
                label=""
                placeholder="Search for a possibility..."
                lifecycleStates={[]}
                allowEmpty={false}
                allowCreate
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Elaboration type</label>
            <div className="space-y-2">
              {ELABORATION_SUBTYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSubtype(t.value)}
                  className={`w-full p-3  border-2 text-left ${
                    subtype === t.value ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="font-medium text-sm text-[var(--text-pri)]">{t.label}</div>
                  <div className="text-xs text-[var(--text-ter)] mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          {showFrameScope && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Applies to which framings of the parent?{' '}
                <span className="text-[var(--text-ter)] font-normal">(optional — defaults to all)</span>
              </label>
              <p className="text-xs text-[var(--text-ter)] mb-2">
                The parent carries multiple framings. Limit this option to the framings under which
                it is a valid possibility; leave all unselected to apply under every framing.
              </p>
              <div className="flex flex-wrap gap-2">
                {parentFrameTypes.map((ft) => {
                  const selected = frameScope.includes(ft);
                  return (
                    <button
                      key={ft}
                      type="button"
                      onClick={() =>
                        setFrameScope((prev) =>
                          prev.includes(ft) ? prev.filter((t) => t !== ft) : [...prev, ft],
                        )
                      }
                      className={`px-3 py-1.5 rounded-full border text-sm font-medium capitalize transition-colors ${
                        selected
                          ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                          : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] hover:border-[var(--rule)]'
                      }`}
                    >
                      {ft}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Rationale (optional)</label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Why is this an elaboration relationship?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canSubmit}
            className="px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Adding...' : 'Add elaboration'}
          </button>
        </div>
      </div>
    </div>
  );
}
